import {
  AdminJobSeekerType,
  AuthAccountUserOrderByChoices,
  GetJobSeekersQueryVariables,
  useGetJobSeekersQuery,
} from "@graphql/generated";
import { IFormData, IFormDataNormalized } from "@modules/TalentPool/List/searchBoxFormConfig";
import React from "react";

export const useTalentPoolData = () => {
  const [state, setState] = React.useState({
    page: 1,
    pageSize: 10,
    filters: {} as Partial<IFormDataNormalized>,
    loading: false,
  });

  const { data, loading: isJobSeekersLoading, refetch: refetchJobSeekers } = useGetJobSeekersQuery();

  const normalizeFormData = (formData: IFormData): IFormDataNormalized => {
    return {
      visaStatus: formData.visaStatus?.map((item) => item.value),
      educationDegree: formData.educationDegree?.map((item) => item.value),
      email: formData.email,
      fullName: formData.fullName,
      birthDate: formData.birthDate,
      country: formData.country,
      city: formData.city,
      phoneNumber: formData.phoneNumber,
      gender: formData.gender?.map((item) => item.value),
      industry: formData.industry,
      interestedJobs: formData.interestedJobs,
      nativeLanguage: formData.nativeLanguage?.map((item) => item.value),
      skills: formData.skills,
      workexperiencesGrade: formData.workexperiencesGrade?.map((item) => item.value),
      jobCitiesId: formData.jobCitiesId,
      jobType: formData.jobType?.map((item) => item.value),
      fluentLanguages: formData.fluentLanguages?.map((item) => item.value) || [],
      referredJobPositionsId: formData.referredJobPositionsId,
      isAssessmentsDone: formData.isAssessmentsDone?.value,
      orderBy: formData.orderBy?.value,
    };
  };

  const getFilterVariables = (
    filters: Partial<IFormDataNormalized>,
    page: number,
    pageSize: number,
  ): GetJobSeekersQueryVariables => ({
    visaStatus: filters.visaStatus?.length ? filters.visaStatus : undefined,
    educationDegree: filters.educationDegree?.length ? filters.educationDegree : undefined,
    email: filters.email?.length ? filters.email : undefined,
    fullName: filters.fullName?.length ? filters.fullName : undefined,
    birthDateGte: filters.birthDate?.[0] || undefined,
    birthDateLte: filters.birthDate?.[1] || undefined,
    countryId: filters.country?.length ? filters.country?.map((country) => country.node.id) : undefined,
    cityId: filters.city?.length ? filters.city?.map((city) => city.node.id) : undefined,
    phoneNumber: filters.phoneNumber || undefined,
    gender: filters.gender?.length ? filters.gender : undefined,
    industryId: filters.industry?.length ? filters.industry?.map((industry) => industry.node.id) : undefined,
    interestedJobsId: filters.interestedJobs?.length ? filters.interestedJobs?.map((job) => job.node.id) : undefined,
    nativeLanguage: filters.nativeLanguage?.length ? filters.nativeLanguage : undefined,
    skillsId: filters.skills?.length ? filters.skills?.map((skill) => skill.node.id) : undefined,
    workexperiencesGrade: filters.workexperiencesGrade?.length ? filters.workexperiencesGrade : undefined,
    first: pageSize,
    offset: (page - 1) * pageSize,
    jobCitiesId: filters.jobCitiesId?.length ? filters.jobCitiesId?.map((city) => city.node.id) : undefined,
    jobType: filters.jobType?.length ? filters.jobType : undefined,
    fluentLanguages: filters.fluentLanguages?.length ? filters.fluentLanguages : undefined,
    referredJobPositionsId: filters.referredJobPositionsId?.length
      ? filters.referredJobPositionsId?.map((job) => job.node.id)
      : undefined,
    isAssessmentsDone: filters.isAssessmentsDone,
    orderBy: filters.orderBy || AuthAccountUserOrderByChoices.ID,
  });

  const refetchWithFilters = React.useCallback(
    async (
      filters: Partial<IFormDataNormalized> = state.filters,
      page: number = state.page,
      pageSize: number = state.pageSize,
    ) => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        await refetchJobSeekers(getFilterVariables(filters, page, pageSize));
      } finally {
        setState((prev) => ({ ...prev, loading: false }));
      }
    },
    [refetchJobSeekers, state.filters, state.page, state.pageSize],
  );

  const handleFilters = (formData: IFormData) => {
    const normalizedData = normalizeFormData(formData);
    setState((prev) => ({ ...prev, filters: normalizedData, page: 1 }));
    refetchWithFilters(normalizedData, 1, state.pageSize);
  };

  const handlePageChange = (newPage: number) => {
    setState((prev) => ({ ...prev, page: newPage }));
    refetchWithFilters(state.filters, newPage, state.pageSize);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setState((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
    refetchWithFilters(state.filters, 1, newPageSize);
  };

  return {
    jobSeekers: data?.admin?.jobSeekers?.edges.map((edge) => edge?.node) as AdminJobSeekerType[],
    totalCount: data?.admin?.jobSeekers?.totalCount || 0,
    loading: state.loading || isJobSeekersLoading,
    page: state.page,
    pageSize: state.pageSize,
    handleFilters,
    handlePageChange,
    handlePageSizeChange,
  };
};
